import { announcements } from "./announcements";
import { articles } from "./articles";
import { courses } from "./courses";
import { downloads } from "./downloads";
import { episodes } from "./episodes";
import { events } from "./events";
import { legacyRoutes } from "./legacyRoutes";
import { getAllMediaSeasons } from "./mediaCycles";
import { routeSectionSlugs } from "./navigation";
import { programs } from "./programs";
import { seasons } from "./seasons";
import { DEFAULT_LOCALE, localeCodes } from "../i18n/locales.js";
import { getSectionKeyFromSlug, resolveRoute } from "../lib/resolveRoute.js";

export const collections = {
  programs,
  seasons,
  episodes,
  courses,
  events,
  articles,
  downloads
};

export function getById(collection, id) {
  const item = collection.find((entry) => entry.id === id);
  if (!item) {
    throw new Error(`Missing data id: ${id}`);
  }
  return item;
}

export function findBySlug(collection, slug, lang = DEFAULT_LOCALE) {
  return collection.find((entry) => entry.slug?.[lang] === slug);
}

export function getSeasonsForProgram(programId) {
  return seasons.filter((season) => season.programId === programId).sort((a, b) => a.seasonNumber - b.seasonNumber);
}

export function getEpisodesForSeason(seasonId) {
  return episodes.filter((episode) => episode.seasonId === seasonId).sort((a, b) => a.episodeNumber - b.episodeNumber);
}

export function getActiveAnnouncement(now = new Date()) {
  const active = announcements
    .filter((item) => {
      if (item.status !== "active") return false;
      const starts = item.startDate ? new Date(item.startDate) <= now : true;
      const ends = item.endDate ? new Date(item.endDate) >= now : true;
      return starts && ends;
    })
    .sort((a, b) => b.priority - a.priority);

  return active[0];
}

export function getLegacyRoute(pathname) {
  return legacyRoutes.find((route) => route.legacyPath === pathname);
}

export function buildSlugIndex() {
  const index = {};
  for (const locale of localeCodes) {
    index[locale] = {};
    for (const [collectionKey, collection] of Object.entries(collections)) {
      index[locale][collectionKey] = Object.fromEntries(collection.map((entry) => [entry.slug[locale], entry.id]));
    }
  }
  return index;
}

export function getEquivalentPath(targetLang, pathname) {
  const segments = pathname.split("/").filter(Boolean);
  const sourceLang = segments[0] || DEFAULT_LOCALE;
  const sectionSlug = segments[1];

  if (!sectionSlug) {
    return resolveRoute(targetLang, "home");
  }

  const sectionKey = getSectionKeyFromSlug(sourceLang, sectionSlug);
  if (!sectionKey) {
    return resolveRoute(targetLang, "home");
  }

  if (segments.length === 2) {
    return resolveRoute(targetLang, sectionKey);
  }

  if (sectionKey === "programs" && segments.length === 4) {
    const program = findBySlug(programs, segments[2], sourceLang);
    const season = findBySlug(seasons, segments[3], sourceLang);
    if (program && season) {
      return resolveRoute(targetLang, "programs", {
        programSlug: program.slug[targetLang],
        seasonSlug: season.slug[targetLang]
      });
    }
  }

  const collectionBySection = {
    programs,
    media: getAllMediaSeasons(),
    episodes,
    courses,
    events,
    articles,
    downloads
  };

  const collection = collectionBySection[sectionKey];
  const item = collection ? findBySlug(collection, segments[2], sourceLang) : undefined;

  if (!item) {
    return resolveRoute(targetLang, sectionKey);
  }

  return resolveRoute(targetLang, sectionKey, { slug: item.slug[targetLang] });
}
